/**
 * LMSMunPrepHubPage.jsx — Diplomexa Readiness Coach
 * ====================================================
 * Sprint LMS-X — MUN Prep Hub.
 *
 * One unified premium console with 5 Diploks-powered tools:
 *   - Position Paper Studio  → POST /api/lms/premium/position-paper-review
 *   - Speech Practice Studio → POST /api/lms/premium/speech-feedback
 *   - Country Briefing Room  → POST /api/lms/premium/research-brief
 *   - Resolution Sandbox     → POST /api/lms/premium/resolution-review
 *   - Readiness Report       → POST /api/lms/premium/readiness-report
 *
 * UX notes:
 *  - Premium · Uses Diploks badge on every tool card.
 *  - First call triggers the one-time Diploks Usage consent modal.
 *  - 402 insufficient-balance fires the LowBalanceDialog.
 *  - Outputs are rendered as a "Diplomexa Intelligence" card — never
 *    mentions any AI provider name.
 *  - Live Diploks balance refreshes after every consume via the
 *    `diploks:wallet-changed` event already wired in lib/diploksClient.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { showLowBalance } from "../../components/DiploksLowBalanceDialog";
import { requestDiploksConsent } from "../../components/DiploksConsentModal";
import { toast } from "sonner";
import {
  ScrollText, Mic2, Globe2, ShieldHalf, Crown, Sparkles,
  Coins, Copy, ArrowLeft, CheckCircle2, Loader2, Target,
  Trophy, GraduationCap, Compass, Download,
} from "lucide-react";
import DiplomexaIntelligenceOutputRenderer from "../../components/diplomexa-intelligence/DiplomexaIntelligenceOutput";

const TABS = [
  { key: "paper",      label: "Position Paper Studio", icon: ScrollText, feature: "lms_position_paper_review",
    blurb: "Get a senior-chair-grade review of your position paper across structure, evidence, diplomacy and country stance." },
  { key: "speech",     label: "Speech Practice Studio", icon: Mic2,      feature: "lms_speech_feedback",
    blurb: "Paste your opening speech transcript and receive feedback on clarity, persuasion and diplomatic tone." },
  { key: "country",    label: "Country Briefing Room",  icon: Globe2,     feature: "lms_country_research_brief",
    blurb: "Generate a 1-page research brief covering foreign policy stance, allies/opponents and talking points." },
  { key: "resolution", label: "Resolution Sandbox",     icon: ShieldHalf, feature: "lms_resolution_review",
    blurb: "Score a draft resolution on clause strength, feasibility and likelihood of passing." },
  { key: "report",     label: "Personal Readiness Report", icon: Crown,   feature: "lms_readiness_report",
    blurb: "Generate a school-safe Readiness Report summarising your strongest and weakest diplomatic skills." },
];

export default function LMSMunPrepHubPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = searchParams.get("tab") || "paper";
  const [activeTab, setActiveTab] = useState(initial);
  const [premiumTools, setPremiumTools] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [balance, setBalance] = useState(null);

  // Refresh tools / balance / readiness
  const reloadMeta = async () => {
    try {
      const r = await api.get("/lms/dashboard");
      setPremiumTools(r.data.premium_tools || []);
      setReadiness(r.data.readiness || null);
      setBalance(r.data.diploks_balance || null);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    reloadMeta();
    const onBal = () => reloadMeta();
    window.addEventListener("diploks:wallet-changed", onBal);
    return () => window.removeEventListener("diploks:wallet-changed", onBal);
  }, []);

  const costFor = useMemo(() => {
    const map = {};
    (premiumTools || []).forEach(t => { map[t.key] = t.cost; });
    return map;
  }, [premiumTools]);

  return (
    <DashboardLayout title="MUN Prep Hub">
      <div data-testid="lmsx-mun-prep-hub" className="space-y-6 pb-12">
        {/* Hero */}
        <section
          data-testid="lmsx-mun-prep-hero"
          className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-8 shadow-2xl text-white"
        >
          <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-[#0F4C81]/40 blur-3xl" />
          <div className="relative">
            <button
              onClick={() => navigate("/dashboard/lms")}
              data-testid="lmsx-mun-prep-back"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]/90 hover:text-[#D4AF37]"
            >
              <ArrowLeft size={12} /> Back to Academy
            </button>
            <p className="mt-3 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
              <GraduationCap size={12} /> Diplomexa Readiness Coach
            </p>
            <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
              MUN Prep Hub
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-blue-100">
              Premium prep tools powered by Diplomexa Intelligence — designed
              to lift your committee readiness from delegate-novice to
              chair-grade diplomat.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4 max-w-2xl">
              <Mini icon={Target} label="Readiness" value={readiness?.overall_score ?? "—"} suffix="/100" />
              <Mini icon={Trophy} label="Tier" value={readiness?.tier || "Novice"} />
              <Mini icon={Coins} label="Diploks" value={balance?.total_available ?? 0} />
              <Mini icon={Sparkles} label="Premium" value={`${TABS.length} tools`} />
            </div>
          </div>
        </section>

        {/* Tab nav */}
        <div className="overflow-x-auto no-scrollbar">
          <div data-testid="lmsx-mun-prep-tabs" className="inline-flex gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`lmsx-tab-${t.key}`}
                  onClick={() => setActiveTab(t.key)}
                  className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-[12px] font-semibold whitespace-nowrap transition ${
                    active
                      ? "bg-[#0A192F] text-white shadow"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={14} className={active ? "text-[#D4AF37]" : "text-[#0F4C81]"} />
                  {t.label}
                  <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-[#D4AF37] text-[#0A192F]" : "bg-slate-100 text-slate-600"}`}>
                    {costFor[t.feature] ?? "—"} Diploks
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab body */}
        {activeTab === "paper"      && <PositionPaperTool      cost={costFor.lms_position_paper_review} />}
        {activeTab === "speech"     && <SpeechTool             cost={costFor.lms_speech_feedback} />}
        {activeTab === "country"    && <CountryTool            cost={costFor.lms_country_research_brief} />}
        {activeTab === "resolution" && <ResolutionTool         cost={costFor.lms_resolution_review} />}
        {activeTab === "report"     && <ReadinessReportTool    cost={costFor.lms_readiness_report} />}

        {/* Compliance footer */}
        <div data-testid="lmsx-prep-policy" className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <p className="text-[11px] text-slate-600 leading-relaxed">
            <span className="font-semibold text-[#0A192F]">Diploks</span> are
            Diplomexa platform credits — non-cash, non-transferable, non-withdrawable.
            <span className="font-semibold text-[#0A192F]"> Podium Points</span> are a
            separate gamification reward and are not deducted by these premium tools.
            All outputs are produced by Diplomexa Intelligence and may be reviewed by your chair or faculty advisor.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Mini({ icon: Icon, label, value, suffix }) {
  return (
    <div className="rounded-xl border border-white/15 bg-white/10 px-3 py-2 backdrop-blur-sm">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-blue-100">
        <Icon size={11} /> {label}
      </div>
      <div className="mt-0.5 text-xl font-bold tabular-nums">
        {value}<span className="text-xs font-normal text-blue-100">{suffix || ""}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Reusable plumbing for premium consume → call API → render output
// ============================================================================
async function runPremium({ endpoint, body, featureKey, featureLabel, setLoading, setOutput }) {
  // Ensure consent up-front (engine will also block, but UX feels nicer)
  try {
    const status = await api.get("/diploks/usage-consent/status");
    if (status.data?.needs_consent) {
      const ok = await requestDiploksConsent();
      if (!ok?.ok) return;
    }
  } catch { /* fall through — let the engine surface */ }
  setLoading(true);
  setOutput(null);
  try {
    const r = await api.post(endpoint, body);
    // Engine deduction succeeded — refresh widget
    window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
    const consume = r.data.consume || {};
    if (consume.cost && !consume.duplicate) {
      toast.success(`${consume.cost} Diploks used`, {
        description: `Balance: ${consume.balance_after}`,
      });
    }
    setOutput(r.data.output || r.data);
  } catch (e) {
    const status = e?.response?.status;
    if (status === 409) {
      // Consent required — open the consent modal, then retry once after accept.
      const ok = await requestDiploksConsent();
      if (ok?.ok) {
        try {
          const r2 = await api.post(endpoint, body);
          window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
          const consume = r2.data.consume || {};
          if (consume.cost && !consume.duplicate) {
            toast.success(`${consume.cost} Diploks used`, {
              description: `Balance: ${consume.balance_after}`,
            });
          }
          setOutput(r2.data.output || r2.data);
          return;
        } catch (e2) {
          if (e2?.response?.status === 402) {
            try {
              const meta = await api.get("/lms/dashboard");
              const have = meta.data?.diploks_balance?.total_available ?? 0;
              const tool = (meta.data?.premium_tools || []).find(t => t.key === featureKey);
              const cost = tool?.cost ?? 0;
              showLowBalance({
                feature_key: featureKey, feature_label: featureLabel,
                cost, balance_available: have, payer_wallet_scope: "personal",
              });
            } catch {
              showLowBalance({ feature_key: featureKey, feature_label: featureLabel });
            }
            return;
          }
          toast.error(formatApiError(e2));
          return;
        }
      }
      toast.warning("Diploks usage policy not accepted yet.");
    } else if (status === 402) {
      try {
        const meta = await api.get("/lms/dashboard");
        const have = meta.data?.diploks_balance?.total_available ?? 0;
        const tool = (meta.data?.premium_tools || []).find(t => t.key === featureKey);
        const cost = tool?.cost ?? 0;
        showLowBalance({
          feature_key: featureKey, feature_label: featureLabel,
          cost, balance_available: have, payer_wallet_scope: "personal",
        });
      } catch {
        showLowBalance({ feature_key: featureKey, feature_label: featureLabel });
      }
    } else if (status === 429) {
      toast.error("You're going too fast — please wait a few seconds.");
    } else {
      toast.error(formatApiError(e));
    }
  } finally {
    setLoading(false);
  }
}

function OutputCard({ output, testid }) {
  if (!output) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(output.content || "");
      toast.success("Copied to clipboard");
    } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div
      data-testid={testid || "lmsx-output-card"}
      className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-white shadow-lg"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#0A192F] via-[#0F4C81] to-[#D4AF37]" />
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-[#D4AF37]">
            <Sparkles size={14} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#0F4C81]">
              {output.brand || "Diplomexa Intelligence"}
            </p>
            <p className="text-sm font-semibold text-[#0A192F]">
              {output.subtitle || "AI-assisted by your Diplomexa Readiness Coach"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={copy}
          data-testid="lmsx-output-copy"
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-[#D4AF37] hover:text-[#0A192F]"
        >
          <Copy size={11} /> Copy
        </button>
      </div>
      <div className="px-5 pb-5">
        <article className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-700">
          {output.content || JSON.stringify(output.readiness_snapshot || {}, null, 2)}
        </article>
        <div className="mt-3 flex items-center gap-2 text-[11px] text-emerald-700">
          <CheckCircle2 size={12} /> Output saved to your Personal Readiness Vault.
        </div>
      </div>
    </div>
  );
}

function ToolHeader({ icon: Icon, title, cost, blurb }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-[#D4AF37] shadow">
            <Icon size={20} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">
              Premium · Uses Diploks
            </p>
            <h2 className="text-lg font-bold text-[#0A192F]">{title}</h2>
          </div>
        </div>
        <span className="rounded-full bg-[#0A192F] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#D4AF37]">
          {cost ?? "—"} Diploks
        </span>
      </div>
      <p className="mt-3 text-sm text-slate-600">{blurb}</p>
    </div>
  );
}

const FIELD = "block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] placeholder-slate-400 focus:border-[#0F4C81] focus:ring-2 focus:ring-[#0F4C81]/20";
const LABEL = "text-[11px] uppercase tracking-[0.22em] font-bold text-slate-600";

// ============================================================================
// Tool 1 — Position Paper Studio
// ============================================================================
function PositionPaperTool({ cost }) {
  const [form, setForm] = useState({ title: "", country: "", committee: "", agenda: "", body: "" });
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const meta = TABS.find(t => t.key === "paper");
  const submit = (e) => {
    e?.preventDefault?.();
    if (form.body.length < 50) {
      toast.error("Position paper must be at least 50 characters.");
      return;
    }
    runPremium({
      endpoint: "/lms/premium/position-paper-review",
      body: form,
      featureKey: "lms_position_paper_review",
      featureLabel: "Position Paper Review",
      setLoading, setOutput,
    });
  };
  return (
    <div data-testid="lmsx-tool-paper-pane" className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      <div className="lg:col-span-3 space-y-4">
        <ToolHeader icon={ScrollText} title={meta.label} cost={cost} blurb={meta.blurb} />
        <form
          onSubmit={submit}
          data-testid="lmsx-form-paper"
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={LABEL}>Paper title</label>
              <input
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="lmsx-field-paper-title"
                className={`mt-1 ${FIELD}`}
                placeholder="e.g. Climate financing reform"
                required minLength={2} maxLength={400}
              />
            </div>
            <div>
              <label className={LABEL}>Country represented</label>
              <input
                value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="lmsx-field-paper-country"
                className={`mt-1 ${FIELD}`}
                placeholder="e.g. Brazil" required
              />
            </div>
            <div>
              <label className={LABEL}>Committee</label>
              <input
                value={form.committee} onChange={(e) => setForm({ ...form, committee: e.target.value })}
                data-testid="lmsx-field-paper-committee"
                className={`mt-1 ${FIELD}`}
                placeholder="e.g. UNGA Third Committee" required
              />
            </div>
            <div>
              <label className={LABEL}>Agenda</label>
              <input
                value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })}
                data-testid="lmsx-field-paper-agenda"
                className={`mt-1 ${FIELD}`}
                placeholder="e.g. Combating climate-induced displacement"
                required
              />
            </div>
          </div>
          <div>
            <label className={LABEL}>Position paper draft</label>
            <textarea
              value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="lmsx-field-paper-body"
              className={`mt-1 ${FIELD} min-h-[260px]`}
              placeholder="Paste your full position paper here (min 50 characters)…"
              required minLength={50} maxLength={20000}
            />
            <p className="mt-1 text-[11px] text-slate-500">{form.body.length} / 20,000 chars · min 50</p>
          </div>
          <SubmitButton loading={loading} cost={cost} testid="lmsx-submit-paper" label="Review Paper" />
        </form>
      </div>
      <div className="lg:col-span-2 space-y-3">
        {loading ? <LoadingCard /> : output ? (
          <DiplomexaIntelligenceOutputRenderer
            output={output}
            title="Position Paper"
            emphasis="review"
            subtitle="Senior-chair-grade feedback across structure, evidence and diplomacy."
            icon={ScrollText}
            diploksUsed={cost}
            testid="lmsx-output-paper"
          />
        ) : <PlaceholderCard />}
      </div>
    </div>
  );
}

// ============================================================================
// Tool 2 — Speech Practice Studio
// ============================================================================
function SpeechTool({ cost }) {
  const [form, setForm] = useState({ transcript: "", country: "", topic: "", duration_seconds: 90 });
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const meta = TABS.find(t => t.key === "speech");
  const submit = (e) => {
    e?.preventDefault?.();
    if (form.transcript.length < 20) {
      toast.error("Transcript must be at least 20 characters.");
      return;
    }
    runPremium({
      endpoint: "/lms/premium/speech-feedback",
      body: { ...form, duration_seconds: parseInt(form.duration_seconds || 90, 10) },
      featureKey: "lms_speech_feedback",
      featureLabel: "Speech Feedback",
      setLoading, setOutput,
    });
  };
  return (
    <div data-testid="lmsx-tool-speech-pane" className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      <div className="lg:col-span-3 space-y-4">
        <ToolHeader icon={Mic2} title={meta.label} cost={cost} blurb={meta.blurb} />
        <form
          onSubmit={submit}
          data-testid="lmsx-form-speech"
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className={LABEL}>Country</label>
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="lmsx-field-speech-country" className={`mt-1 ${FIELD}`} required placeholder="Brazil" />
            </div>
            <div className="md:col-span-1">
              <label className={LABEL}>Topic</label>
              <input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                data-testid="lmsx-field-speech-topic" className={`mt-1 ${FIELD}`} required placeholder="Opening speech" />
            </div>
            <div>
              <label className={LABEL}>Duration (s)</label>
              <input
                type="number" min={10} max={900}
                value={form.duration_seconds}
                onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })}
                data-testid="lmsx-field-speech-duration"
                className={`mt-1 ${FIELD}`} required
              />
            </div>
          </div>
          <div>
            <label className={LABEL}>Speech transcript</label>
            <textarea
              value={form.transcript} onChange={(e) => setForm({ ...form, transcript: e.target.value })}
              data-testid="lmsx-field-speech-transcript"
              className={`mt-1 ${FIELD} min-h-[200px]`}
              placeholder="Paste your speech transcript here (min 20 characters)…"
              required minLength={20} maxLength={20000}
            />
            <p className="mt-1 text-[11px] text-slate-500">{form.transcript.length} / 20,000 chars · min 20</p>
          </div>
          <SubmitButton loading={loading} cost={cost} testid="lmsx-submit-speech" label="Get speech feedback" />
        </form>
      </div>
      <div className="lg:col-span-2 space-y-3">
        {loading ? <LoadingCard /> : output ? (
          <DiplomexaIntelligenceOutputRenderer
            output={output}
            title="Speech Practice"
            emphasis="feedback"
            subtitle="Clarity, confidence, diplomatic tone and timing."
            icon={Mic2}
            kicker="Diplomexa Intelligence"
            diploksUsed={cost}
            testid="lmsx-output-speech"
          />
        ) : <PlaceholderCard />}
      </div>
    </div>
  );
}

// ============================================================================
// Tool 3 — Country Briefing Room
// ============================================================================
function CountryTool({ cost }) {
  const [form, setForm] = useState({ country: "", agenda: "", committee: "" });
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const meta = TABS.find(t => t.key === "country");
  const submit = (e) => {
    e?.preventDefault?.();
    runPremium({
      endpoint: "/lms/premium/research-brief",
      body: form,
      featureKey: "lms_country_research_brief",
      featureLabel: "Country Research Brief",
      setLoading, setOutput,
    });
  };
  return (
    <div data-testid="lmsx-tool-country-pane" className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      <div className="lg:col-span-3 space-y-4">
        <ToolHeader icon={Globe2} title={meta.label} cost={cost} blurb={meta.blurb} />
        <form onSubmit={submit} data-testid="lmsx-form-country"
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className={LABEL}>Country</label>
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="lmsx-field-country-name" className={`mt-1 ${FIELD}`} required placeholder="India" />
            </div>
            <div>
              <label className={LABEL}>Committee</label>
              <input value={form.committee} onChange={(e) => setForm({ ...form, committee: e.target.value })}
                data-testid="lmsx-field-country-committee" className={`mt-1 ${FIELD}`} required placeholder="UNSC" />
            </div>
            <div>
              <label className={LABEL}>Agenda</label>
              <input value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })}
                data-testid="lmsx-field-country-agenda" className={`mt-1 ${FIELD}`} required placeholder="Peacekeeping reform" />
            </div>
          </div>
          <SubmitButton loading={loading} cost={cost} testid="lmsx-submit-country" label="Generate Briefing" />
        </form>
      </div>
      <div className="lg:col-span-2 space-y-3">
        {loading ? <LoadingCard /> : output ? (
          <DiplomexaIntelligenceOutputRenderer
            output={output}
            title="Country"
            emphasis="briefing"
            subtitle="Foreign policy, allies, opposing blocs and useful diplomatic phrases."
            icon={Globe2}
            kicker="Global Delegate Brief"
            diploksUsed={cost}
            testid="lmsx-output-country"
          />
        ) : <PlaceholderCard />}
      </div>
    </div>
  );
}

// ============================================================================
// Tool 4 — Resolution Sandbox
// ============================================================================
function ResolutionTool({ cost }) {
  const [form, setForm] = useState({ committee: "", agenda: "", resolution_text: "" });
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const meta = TABS.find(t => t.key === "resolution");
  const submit = (e) => {
    e?.preventDefault?.();
    if (form.resolution_text.length < 50) {
      toast.error("Draft must be at least 50 characters.");
      return;
    }
    runPremium({
      endpoint: "/lms/premium/resolution-review",
      body: form,
      featureKey: "lms_resolution_review",
      featureLabel: "Resolution Review",
      setLoading, setOutput,
    });
  };
  return (
    <div data-testid="lmsx-tool-resolution-pane" className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      <div className="lg:col-span-3 space-y-4">
        <ToolHeader icon={ShieldHalf} title={meta.label} cost={cost} blurb={meta.blurb} />
        <form onSubmit={submit} data-testid="lmsx-form-resolution"
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={LABEL}>Committee</label>
              <input value={form.committee} onChange={(e) => setForm({ ...form, committee: e.target.value })}
                data-testid="lmsx-field-resolution-committee" className={`mt-1 ${FIELD}`} required />
            </div>
            <div>
              <label className={LABEL}>Agenda</label>
              <input value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })}
                data-testid="lmsx-field-resolution-agenda" className={`mt-1 ${FIELD}`} required />
            </div>
          </div>
          <div>
            <label className={LABEL}>Draft resolution</label>
            <textarea
              value={form.resolution_text}
              onChange={(e) => setForm({ ...form, resolution_text: e.target.value })}
              data-testid="lmsx-field-resolution-text"
              className={`mt-1 ${FIELD} min-h-[240px]`}
              placeholder="Paste your draft resolution clauses (min 50 characters)…"
              required minLength={50} maxLength={20000}
            />
            <p className="mt-1 text-[11px] text-slate-500">{form.resolution_text.length} / 20,000 chars · min 50</p>
          </div>
          <SubmitButton loading={loading} cost={cost} testid="lmsx-submit-resolution" label="Review Resolution" />
        </form>
      </div>
      <div className="lg:col-span-2 space-y-3">
        {loading ? <LoadingCard /> : output ? (
          <DiplomexaIntelligenceOutputRenderer
            output={output}
            title="Resolution"
            emphasis="review"
            subtitle="Clause quality, formatting, feasibility, diplomatic language and committee fit."
            icon={ShieldHalf}
            kicker="Committee Readiness Brief"
            diploksUsed={cost}
            testid="lmsx-output-resolution"
          />
        ) : <PlaceholderCard />}
      </div>
    </div>
  );
}

// ============================================================================
// Tool 5 — Personal Readiness Report
// ============================================================================
function ReadinessReportTool({ cost }) {
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const meta = TABS.find(t => t.key === "report");

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { getMyReadinessReports } = await import("../../lib/trainerGradingApi");
      const j = await getMyReadinessReports(10);
      setHistory(j.rows || []);
    } catch { /* non-fatal */ }
    finally { setHistoryLoading(false); }
  };
  useEffect(() => { loadHistory(); }, []);

  const submit = () => {
    runPremium({
      endpoint: "/lms/premium/readiness-report",
      body: {},
      featureKey: "lms_readiness_report",
      featureLabel: "Readiness Report",
      setLoading,
      setOutput: (out) => { setOutput(out); loadHistory(); },
    });
  };
  return (
    <div data-testid="lmsx-tool-report-pane" className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      <div className="lg:col-span-3 space-y-4">
        <ToolHeader icon={Crown} title={meta.label} cost={cost} blurb={meta.blurb} />
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-700">
            We will analyse your progress, papers, speeches and quizzes to generate
            a school-safe Personal Readiness Report you can share with chairs and faculty.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div className="rounded-lg border border-slate-200 px-3 py-2">
              <Compass size={12} className="text-[#0F4C81] inline" /> 6-skill readiness
            </div>
            <div className="rounded-lg border border-slate-200 px-3 py-2">
              <Sparkles size={12} className="text-[#D4AF37] inline" /> Diplomexa Intelligence narrative
            </div>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={loading}
            data-testid="lmsx-submit-report"
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#0A192F] px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50] disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Generate Readiness Report · {cost ?? "—"} Diploks
          </button>
        </div>

        {/* Past reports history with PDF export */}
        <ReadinessReportHistory rows={history} loading={historyLoading} />
      </div>
      <div className="lg:col-span-2 space-y-3">
        {loading ? <LoadingCard /> : output ? (
          <DiplomexaIntelligenceOutputRenderer
            output={output}
            title="Personal Readiness"
            emphasis="report"
            subtitle="Your strongest skills, gaps, and a focused 7-day plan."
            icon={Crown}
            kicker="Diplomexa Readiness Coach"
            diploksUsed={cost}
            testid="lmsx-output-report"
          />
        ) : <PlaceholderCard />}
      </div>
    </div>
  );
}

function ReadinessReportHistory({ rows, loading }) {
  return (
    <section
      data-testid="readiness-history-card"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] uppercase tracking-[0.22em] text-[#0A192F] font-bold">
          Past Readiness Reports
        </h3>
        <span className="text-[10px] text-slate-400">Last 10</span>
      </div>
      {loading ? (
        <p data-testid="readiness-history-loading" className="mt-4 text-xs text-slate-500">Loading history…</p>
      ) : !rows.length ? (
        <p data-testid="readiness-history-empty" className="mt-4 text-xs text-slate-500">
          You haven&apos;t generated any reports yet. Run one above to start your archive.
        </p>
      ) : (
        <ol data-testid="readiness-history-list" className="mt-3 space-y-1.5">
          {rows.map((r) => {
            const tier = (r.readiness || {}).tier || "—";
            const score = (r.readiness || {}).overall_score ?? "—";
            const when = (r.created_at || "").slice(0, 16).replace("T", " ");
            const base = process.env.REACT_APP_BACKEND_URL || "";
            return (
              <li
                key={r.id}
                data-testid={`readiness-history-row-${r.id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-1.5 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#0A192F]">{tier} · {score}/100</p>
                  <p className="text-[10px] text-slate-500">{when}</p>
                </div>
                <a
                  data-testid={`readiness-pdf-${r.id}`}
                  href={`${base}/api/lms/readiness-report/${r.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-md bg-[#D4AF37] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#c69f30]"
                >
                  <Download size={10} /> PDF
                </a>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

// ============================================================================
// Misc UI
// ============================================================================
function SubmitButton({ loading, cost, testid, label }) {
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-lg bg-[#0A192F] px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50] disabled:opacity-50"
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
      {label} · {cost ?? "—"} Diploks
    </button>
  );
}

function LoadingCard() {
  return (
    <div data-testid="lmsx-output-loading" className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
      <Loader2 size={28} className="mx-auto animate-spin text-[#0F4C81]" />
      <p className="mt-3 text-sm font-semibold text-[#0A192F]">Diplomexa Intelligence is preparing your output…</p>
      <p className="mt-1 text-[11px] text-slate-500">This usually takes 6-15 seconds.</p>
    </div>
  );
}

function PlaceholderCard() {
  return (
    <div data-testid="lmsx-output-placeholder" className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
      <Sparkles size={20} className="mx-auto text-[#D4AF37]" />
      <p className="mt-2 text-sm font-semibold text-[#0A192F]">Output will appear here</p>
      <p className="mt-1 text-[11px] text-slate-500">
        Fill the form and submit to get a professional, chair-grade output from Diplomexa Intelligence.
      </p>
    </div>
  );
}
